import { GetterTree, MutationTree, ActionTree, ActionContext } from 'vuex';
import { utils } from '@easygenerator/fragment-sdk';
import { LoggingType } from '@easygenerator/fragment-sdk/dist/utils/logger/constants';
import Config from '../config';
import { ICourse, ICoursesCounters, ICourseOptions, CourseParam } from '../types/types';

const config = Config.getInstance();
const logger = utils.Logger.getInstance();
const HttpWrapper = utils.HttpWrapper;

export const INITIALIZE_COURSES = 'INITIALIZE_COURSES';
export const RESET_COURSES = 'RESET_COURSES';
export const SET_COURSE_OPTION_FILTER = 'SET_COURSE_OPTION_FILTER';
export const SET_COURSE_OPTION_SEARCH = 'SET_COURSE_OPTION_SEARCH';
export const RESET_COURSE_OPTION = 'RESET_COURSE_OPTION';
export const SET_ACTIVE_COURSES_COUNTER = 'SET_ACTIVE_COURSES_COUNTER';
export const SET_COMPLETED_COURSES_COUNTER = 'SET_COMPLETED_COURSES_COUNTER';
export const SET_ACTIVE_LIST_COURSES_COUNTER = 'SET_ACTIVE_LIST_COURSES_COUNTER';
export const SET_COMPLETED_LIST_COURSES_COUNTER = 'SET_COMPLETED_LIST_COURSES_COUNTER';
export const RESET_COURSES_COUNTER = 'RESET_COURSES_COUNTER';
export const LOAD_MORE_COURSES = 'LOAD_MORE_COURSES';
export const ACTIVE_COURSE_LOADER = 'ACTIVE_COURSE_LOADER';
export const DISABLE_COURSE_LOADER = 'DISABLE_COURSE_LOADER';
export const ACTIVE_LOAD_MORE_COURSES = 'ACTIVE_LOAD_MORE_COURSES';
export const DISABLE_LOAD_MORE_COURSES = 'DISABLE_LOAD_MORE_COURSES';

import { DISABLE_COURSE_LOADER as DISABLE_COURSE_LOADER_APP } from './app';

const COURSES_SKIP = 0;
const COURSES_LIMIT = 25;
const COURSES_FILTER = CourseParam.Active;
const COURSES_SORT = 'updatedAt=desc';

export class State {
  courses: ICourse[] = [];

  coursesOptions: ICourseOptions = {
    skip: COURSES_SKIP,
    limit: COURSES_LIMIT,
    filter: COURSES_FILTER,
    search: '',
    sort: COURSES_SORT
  };

  counters: ICoursesCounters = {
    active: 0,
    completed: 0,
    all: 0
  };

  listCounters: ICoursesCounters = {
    active: 0,
    completed: 0,
    all: 0
  };

  isLoading = false;

  isCanLoadMoreCourses = false;
}

const getters = {
  isLoading: (state: State) => state.isLoading,
  isCanLoadMoreCourses: (state: State) => state.isCanLoadMoreCourses,
  courses: (state: State) => state.courses,
  counters: (state: State) => ({ ...state.counters, all: state.counters.active + state.counters.completed }),
  listCounters: (state: State) => ({
    ...state.listCounters,
    all: state.listCounters.active + state.listCounters.completed
  }),
  options: (state: State) => state.coursesOptions
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as GetterTree<State, any>;

const mutations = {
  [INITIALIZE_COURSES](state: State, courses: ICourse[]) {
    state.courses = [...new Map(courses.map(course => [course.courseId, course])).values()];
  },
  [LOAD_MORE_COURSES](state: State, courses: ICourse[]) {
    const mixedCourses = [...state.courses, ...courses];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const filteredCourses = mixedCourses.reduce((acc, item) => ({ ...acc, [item.courseId]: item }), {} as any);
    state.courses = Object.values(filteredCourses);
  },
  [RESET_COURSES](state: State) {
    state.courses = [];
  },

  [SET_COURSE_OPTION_FILTER](state: State, filter: CourseParam) {
    state.coursesOptions.filter = filter;
  },
  [SET_COURSE_OPTION_SEARCH](state: State, search: string) {
    state.coursesOptions.search = search;
  },
  [RESET_COURSE_OPTION](state: State) {
    state.coursesOptions = {
      skip: COURSES_SKIP,
      limit: COURSES_LIMIT,
      filter: COURSES_FILTER,
      search: '',
      sort: COURSES_SORT
    };
  },
  [SET_ACTIVE_COURSES_COUNTER](state: State, count: number) {
    state.counters.active = count;
  },
  [SET_COMPLETED_COURSES_COUNTER](state: State, count: number) {
    state.counters.completed = count;
  },
  [SET_ACTIVE_LIST_COURSES_COUNTER](state: State, count: number) {
    state.listCounters.active = count;
  },
  [SET_COMPLETED_LIST_COURSES_COUNTER](state: State, count: number) {
    state.listCounters.completed = count;
  },
  [RESET_COURSES_COUNTER](state: State) {
    state.counters = { active: 0, completed: 0, all: 0 };
    state.listCounters = { active: 0, completed: 0, all: 0 };
  },

  [ACTIVE_COURSE_LOADER](state: State) {
    state.isLoading = true;
  },
  [DISABLE_COURSE_LOADER](state: State) {
    state.isLoading = false;
  },

  [ACTIVE_LOAD_MORE_COURSES](state: State) {
    state.isCanLoadMoreCourses = true;
  },
  [DISABLE_LOAD_MORE_COURSES](state: State) {
    state.isCanLoadMoreCourses = false;
  }
} as MutationTree<State>;

const actions = {
  async initCourses({ commit, dispatch }) {
    try {
      commit(`app/${ACTIVE_COURSE_LOADER}`, null, { root: true });

      await Promise.all([
        dispatch('courses/getCourses', { isNewSet: true }, { root: true }),
        dispatch('courses/getAllCoursesCounter', {}, { root: true })
      ]);

      commit(`app/${DISABLE_COURSE_LOADER_APP}`, null, { root: true });
    } catch (error) {
      logger.trackError(LoggingType.error, error);
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getCourses({ state, commit, rootGetters }: ActionContext<State, any>, courseOptions: ICourseOptions) {
    const token = rootGetters['app/token'];

    try {
      const {
        filter = state.coursesOptions.filter,
        skip = state.coursesOptions.skip,
        limit = state.coursesOptions.limit,
        search = state.coursesOptions.search,
        isNewSet = false,
        sort = state.coursesOptions.sort
      } = courseOptions;

      const coursesUrl = new URL(
        `${config.learnServiceUrl}/api/learner/me/courses?skip=${skip}&limit=${limit}&filter=${filter}`
      );

      if (search) {
        coursesUrl.searchParams.append('search', search);
      }

      if (sort) {
        const [sortBy, sortOrder] = sort.split('=');
        coursesUrl.searchParams.append('sortBy', sortBy);
        coursesUrl.searchParams.append('sortOrder', sortOrder);
      }

      const courses = await HttpWrapper.get(coursesUrl.toString(), { bearerToken: token });

      if (filter !== state.coursesOptions.filter || isNewSet) {
        commit(SET_COURSE_OPTION_FILTER, filter);
        commit(SET_COURSE_OPTION_SEARCH, search);
        commit(INITIALIZE_COURSES, courses);
      } else {
        commit(LOAD_MORE_COURSES, courses);
      }
    } catch (error) {
      logger.trackError(LoggingType.error, error);
      commit(INITIALIZE_COURSES, []);
    }
  },
  async getCourseCounter(
    { commit, rootGetters }: ActionContext<State, unknown>,
    criteria: { param: CourseParam; search: string }
  ) {
    const token = rootGetters['app/token'];
    const { param, search } = criteria;

    const countUrl = new URL(`${config.learnServiceUrl}/api/learner/me/courses/count`);
    countUrl.searchParams.append('filter', param);

    if (search) {
      countUrl.searchParams.append('search', search);
    }

    try {
      const counter: number | null = await HttpWrapper.get(countUrl.toString(), { bearerToken: token });
      if (counter === null) {
        logger.trackError(LoggingType.error, 'The function getCourseCounter got null on request');
        return;
      }
      if (param === CourseParam.Active) {
        commit(SET_ACTIVE_LIST_COURSES_COUNTER, counter);
        if (!search) {
          commit(SET_ACTIVE_COURSES_COUNTER, counter);
        }
      }
      if (param === CourseParam.Completed) {
        commit(SET_COMPLETED_LIST_COURSES_COUNTER, counter);
        if (!search) {
          commit(SET_COMPLETED_COURSES_COUNTER, counter);
        }
      }
    } catch (error) {
      logger.trackError(LoggingType.error, error);
    }
  },
  async getAllCoursesCounter({ commit, rootGetters }: ActionContext<State, unknown>, criteria: { search: string }) {
    const token = rootGetters['app/token'];
    const { search } = criteria;

    const activeCountUrl = new URL(`${config.learnServiceUrl}/api/learner/me/courses/count`);
    activeCountUrl.searchParams.append('filter', CourseParam.Active);
    const completedCountUrl = new URL(`${config.learnServiceUrl}/api/learner/me/courses/count`);
    completedCountUrl.searchParams.append('filter', CourseParam.Completed);

    if (search) {
      activeCountUrl.searchParams.append('search', search);
      completedCountUrl.searchParams.append('search', search);
    }

    try {
      const counters: number[] = await Promise.all([
        HttpWrapper.get(activeCountUrl.toString(), { bearerToken: token }),
        HttpWrapper.get(completedCountUrl.toString(), { bearerToken: token })
      ]);

      commit(SET_ACTIVE_LIST_COURSES_COUNTER, counters[0]);
      commit(SET_COMPLETED_LIST_COURSES_COUNTER, counters[1]);

      if (!search) {
        commit(SET_ACTIVE_COURSES_COUNTER, counters[0]);
        commit(SET_COMPLETED_COURSES_COUNTER, counters[1]);
      }
    } catch (error) {
      logger.trackError(LoggingType.error, error);
    }
  },
  resetState({ commit }) {
    commit(RESET_COURSES);
    commit(RESET_COURSES_COUNTER);
    commit(RESET_COURSE_OPTION);
    commit(DISABLE_COURSE_LOADER);
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
} as ActionTree<State, any>;

export default {
  namespaced: true,
  state: new State(),
  getters,
  mutations,
  actions
};
